import DOMPurify from 'dompurify';

/**
 * Tags produced by Quill (see quillUtils.ts `formats` + node_modules/quill/formats):
 * header, bold, italic, underline, strike, color, background, list, indent,
 * link, video, image, script (sub/super), mention, tableBlot.
 */
const ALLOWED_TAGS = [
  // Block / structure
  'p', 'br', 'div', 'blockquote', 'pre',
  // Headings (Quill header format)
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Inline text (bold/italic/underline/strike + react-simple-wysiwyg)
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'span', 'small',
  // Lists & indent (ql-indent-* on li/p via class)
  'ul', 'ol', 'li',
  // Links, media (Quill link / image / video)
  'a', 'img', 'iframe',
  // Tables (LLM / API HTML + Quill table blot)
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'col', 'colgroup',
  'code', 'hr',
] as const;

/**
 * Attributes used by Quill blots and CustomImageBlot / CustomLinkBlot.
 * `style` is required for color/background; DOMPurify sanitizes CSS values.
 */
const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'class',
  'style',
  'src', 'alt', 'width', 'height',
  'frameborder', 'allowfullscreen', 'allow', 'scrolling',
  // quill-mention (MentionBlot dataset → data-* on span.mention)
  'data-id', 'data-value', 'data-denotation-char',
  // tables
  'colspan', 'rowspan',
] as const;

const ALLOWED_SVG_TAGS = [
  'svg', 'g', 'path', 'rect', 'circle', 'line', 'polyline', 'polygon',
  'ellipse', 'text', 'tspan', 'defs', 'clippath', 'clipPath', 'mask', 'use',
  'lineargradient', 'linearGradient', 'radialgradient', 'radialGradient', 'stop',
  'filter', 'feBlend', 'feColorMatrix', 'feComponentTransfer', 'feComposite',
  'feConvolveMatrix', 'feDiffuseLighting', 'feDisplacementMap', 'feDistantLight',
  'feDropShadow', 'feFlood', 'feFuncA', 'feFuncB', 'feFuncG', 'feFuncR',
  'feGaussianBlur', 'feImage', 'feMerge', 'feMergeNode', 'feMorphology',
  'feOffset', 'fePointLight', 'feSpecularLighting', 'feSpotLight', 'feTile',
  'feTurbulence', 'animate', 'animategroup', 'animatetrack', 'animatecolor',
  'animatemotion', 'animatetransform', 'desc', 'image', 'marker', 'pattern',
  'symbol', 'textpath', 'textPath', 'title', 'view', 'altglyph', 'altglyphdef',
  'altglyphitem', 'font', 'glyph', 'glyphref', 'hkern', 'metadata', 'mpath',
  'switch', 'tref', 'vkern'
] as const;

const ALLOWED_SVG_ATTR = [
  'accent-height', 'accumulate', 'additive', 'alignment-baseline', 'amplitude', 'ascent',
  'attributename', 'attributetype', 'azimuth', 'basefrequency', 'baseline-shift', 'begin',
  'bias', 'by', 'class', 'clip', 'clippathunits', 'clip-path', 'clip-rule', 'color',
  'color-interpolation', 'color-interpolation-filters', 'color-profile', 'color-rendering',
  'cx', 'cy', 'd', 'dx', 'dy', 'diffuseconstant', 'direction', 'display', 'divisor',
  'dur', 'edgemode', 'elevation', 'end', 'exponent', 'fill', 'fill-opacity', 'fill-rule',
  'filter', 'filterunits', 'flood-color', 'flood-opacity', 'font-family', 'font-size',
  'font-size-adjust', 'font-stretch', 'font-style', 'font-variant', 'font-weight',
  'fx', 'fy', 'g1', 'g2', 'glyph-name', 'glyphref', 'gradientunits', 'gradienttransform',
  'gradientUnits', 'gradientTransform', 'height', 'href', 'xlink:href', 'id', 'image-rendering',
  'in', 'in2', 'intercept', 'k', 'k1', 'k2', 'k3', 'k4', 'kerning', 'keypoints', 'keysplines',
  'keytimes', 'lang', 'lengthadjust', 'letter-spacing', 'kernelmatrix', 'kernelunitlength',
  'lighting-color', 'local', 'marker-end', 'marker-mid', 'marker-start', 'markerheight',
  'markerunits', 'markerwidth', 'markerHeight', 'markerUnits', 'markerWidth', 'maskcontentunits',
  'maskunits', 'max', 'mask', 'mask-type', 'media', 'method', 'mode', 'min', 'name',
  'numoctaves', 'offset', 'operator', 'opacity', 'order', 'orient', 'orientation', 'origin',
  'overflow', 'paint-order', 'path', 'pathlength', 'patterncontentunits', 'patterntransform',
  'patternunits', 'points', 'preservealpha', 'preserveaspectratio', 'preserveAspectRatio',
  'primitiveunits', 'r', 'rx', 'ry', 'radius', 'refx', 'refy', 'repeatcount', 'repeatdur',
  'restart', 'result', 'rotate', 'scale', 'seed', 'shape-rendering', 'slope', 'specularconstant',
  'specularexponent', 'spreadmethod', 'spreadMethod', 'startoffset', 'stddeviation', 'stdDeviation',
  'stitchtiles', 'stop-color', 'stop-opacity', 'stroke-dasharray', 'stroke-dashoffset',
  'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke',
  'stroke-width', 'style', 'surfacescale', 'systemlanguage', 'tabindex', 'tablevalues',
  'targetx', 'targety', 'transform', 'transform-origin', 'text-anchor', 'text-decoration',
  'text-rendering', 'textlength', 'type', 'u1', 'u2', 'unicode', 'values', 'viewbox', 'viewBox',
  'visibility', 'version', 'vert-adv-y', 'vert-origin-x', 'vert-origin-y', 'width', 'word-spacing',
  'wrap', 'writing-mode', 'xchannelselector', 'ychannelselector', 'x', 'x1', 'x2', 'xmlns',
  'y', 'y1', 'y2', 'z', 'zoomandpan'
] as const;

let quillIframeHookRegistered = false;

const registerQuillSanitizeHooks = () => {
  if (quillIframeHookRegistered) return;
  quillIframeHookRegistered = true;

  // Quill video embeds: only https (or protocol-relative) iframe sources
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    const src = (node as Element).getAttribute('src')?.trim() ?? '';
    const safe =
      /^https:\/\//i.test(src) ||
      /^\/\//.test(src) ||
      src === '';
    if (!safe) {
      node.parentNode?.removeChild(node);
    }
  });
};

registerQuillSanitizeHooks();

/** Sanitize HTML before dangerouslySetInnerHTML (Quill, LLM output, API content, translations). */
export const sanitizeHtml = (html: string | null | undefined): string =>
  DOMPurify.sanitize(html ?? '', {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    ALLOW_DATA_ATTR: false,
  });

/** Sanitize AI-generated HTML that may contain a scoped <style> block (e.g. self-contained report divs). */
export const sanitizeAIHtml = (html: string | null | undefined): string =>
  DOMPurify.sanitize(html ?? '', {
    ALLOWED_TAGS: [...ALLOWED_TAGS, 'style', ...ALLOWED_SVG_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR, ...ALLOWED_SVG_ATTR],
    ALLOW_DATA_ATTR: false,
    FORCE_BODY: false,
  });
